/**
 * BO Appendix A/B/C — 설문으로 포함할 회신 양식 선택 (Stage 없음).
 */
import {
  BO_APPENDIX_DOCS,
  DOC_KEY_TO_CODE,
  type BoAppendixDocCode,
  type BoAppendixDocKey,
} from './boAppendixFields';

export type { BoAppendixDocKey };

export interface BoAppendixSurvey {
  selected: BoAppendixDocKey[];
  completed: boolean;
}

export interface BoAppendixOptionMeta {
  id: BoAppendixDocKey;
  label: string;
  hint: string;
  group: 'A' | 'B' | 'C';
  docCode: BoAppendixDocCode;
}

export const emptyBoAppendixSurvey = (): BoAppendixSurvey => ({
  selected: [],
  completed: false,
});

export const APPENDIX_OPTIONS: BoAppendixOptionMeta[] = [
  {
    id: 'appendix_a',
    label: 'Appendix A — Particulars of Beneficial Owner',
    hint: 'Self-declaration: the recipient confirms they are the BO.',
    group: 'A',
    docCode: 'BA01',
  },
  {
    id: 'appendix_b_corporate',
    label: 'Appendix B — Others as BO (Corporate)',
    hint: 'Nominate a new BO. Signed for and on behalf of a corporate member. Fields are independent from the Individual form.',
    group: 'B',
    docCode: 'BA02',
  },
  {
    id: 'appendix_b_individual',
    label: 'Appendix B — Others as BO (Individual)',
    hint: 'Nominate a new BO. Signed by an individual member. Fields are independent from the Corporate form.',
    group: 'B',
    docCode: 'BA03',
  },
  {
    id: 'appendix_c_corporate',
    label: 'Appendix C — Senior Management (Corporate)',
    hint: 'No BO / cannot identify / still obtaining. Directors sign for the corporate member. SM particulars are entered manually (not auto-filled).',
    group: 'C',
    docCode: 'BA04',
  },
  {
    id: 'appendix_c_individual',
    label: 'Appendix C — Senior Management (Individual)',
    hint: 'No BO / cannot identify / still obtaining. Senior Management particulars are entered manually. Senior Management signs. Fields are independent from the Corporate form.',
    group: 'C',
    docCode: 'BA05',
  },
];

const ORDER: BoAppendixDocKey[] = APPENDIX_OPTIONS.map((o) => o.id);

export function normalizeAppendixSurvey(
  raw: Partial<BoAppendixSurvey> | null | undefined,
): BoAppendixSurvey {
  const base = emptyBoAppendixSurvey();
  if (!raw || typeof raw !== 'object') return base;
  const allowed = new Set(APPENDIX_OPTIONS.map((o) => o.id));
  return {
    selected: Array.isArray(raw.selected)
      ? raw.selected.filter((k): k is BoAppendixDocKey => allowed.has(k as BoAppendixDocKey))
      : [],
    completed: !!raw.completed,
  };
}

export function isAppendixSurveyReady(survey: BoAppendixSurvey): boolean {
  return (normalizeAppendixSurvey(survey).selected || []).length > 0;
}

export function resolveIncludedAppendixDocs(
  survey: BoAppendixSurvey | null | undefined,
): BoAppendixDocKey[] {
  const s = normalizeAppendixSurvey(survey);
  if (!s.completed || !s.selected.length) return [];
  const set = new Set(s.selected);
  return ORDER.filter((k) => set.has(k));
}

export function resolveIncludedAppendixCodes(
  survey: BoAppendixSurvey | null | undefined,
): BoAppendixDocCode[] {
  return resolveIncludedAppendixDocs(survey).map((k) => DOC_KEY_TO_CODE[k]);
}

export function summarizeAppendixSurvey(survey: BoAppendixSurvey): string {
  const keys = resolveIncludedAppendixDocs(survey);
  if (!keys.length) return 'No forms selected';
  return keys.map((k) => {
    const code = DOC_KEY_TO_CODE[k];
    return BO_APPENDIX_DOCS[code].label;
  }).join(' · ');
}
